/**
 * WS-B Task 8 — URL-synced list state (spec §2).
 *
 * The URL grammar IS the API grammar: list routes carry
 * `?view=<key>&filter.<col>.<op>=<value>&sort=<col>&dir=<asc|desc>&page=<n>&pageSize=<n>`
 * — the exact keys `ColumnFilters` produces and `ListQuery.FromQueryString` parses, so a
 * copied URL is also a valid API query string.
 *
 * This hook owns parse/serialize of `view`, `sort`+`dir`, `page`, `pageSize`, and all
 * `filter.*` keys. Params it doesn't own pass through untouched. Conventions:
 *
 * - **Defaults are omitted** — page 1, pageSize {@link DEFAULT_PAGE_SIZE}, no sort, and the
 *   default (first system) view never appear as params; absent `view` = the default view.
 * - **Precedence: URL wins** — `selectView` writes `view=<key>` and clears every override
 *   param (clean slate = the view's own definition); subsequent tweaks write explicit params
 *   on top. The view-definition OVERLAY itself (per-key merge) is EntityListPage's job — the
 *   hook only carries raw URL state.
 * - **Page hygiene** — any filter/sort/pageSize/view change removes `page` (back to 1).
 * - **History replace** — same as `useFacetSearchParams`, so twiddling doesn't bloat Back.
 */
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, GROUP_GRANULARITIES, GroupGranularity, ViewGroup, ViewSort } from './viewDefinition';

const FILTER_PREFIX = 'filter.';
/** The non-filter params this hook owns (view is handled separately). */
// `group`/`groupBy` joined 2026-10-20 (fleet contract v2 §11 V1): grouping is list state — it changes
// the order rows appear in and how they are counted — so it lives in the URL and survives Back.
const OVERRIDE_SCALARS = ['sort', 'dir', 'page', 'pageSize', 'group', 'groupBy'] as const;

/** Raw URL list state. `view` is the EFFECTIVE key (absent param ⇒ the default view). */
export interface ListSearchState {
  view: string;
  /** The literal `view` param, `null` when absent (i.e. the default view is active). */
  rawView: string | null;
  /** URL filter overrides, keyed `filter.<col>.<op>` (empty values kept — data ops). */
  filters: Record<string, string>;
  /** Explicit sort override; `null` = defer to the view definition / endpoint default. */
  sort: ViewSort | null;
  page: number;
  /** Explicit pageSize override; `null` = defer to view definition / prop / 50. */
  pageSize: number | null;
  /** The grouped column, or `null` when ungrouped (v2 §11 V1). */
  group: ViewGroup | null;
}

export interface ListSearchParams extends ListSearchState {
  /** Replace ALL `filter.*` params with `next` (resets page). */
  setFilters: (next: Record<string, string>) => void;
  /** Set or clear the explicit sort override (resets page). */
  setSort: (sort: ViewSort | null) => void;
  /** Navigate; 1 (the default) removes the param. */
  setPage: (page: number) => void;
  /**
   * Set the pageSize override (resets page). Selecting `omitWhen` (default
   * {@link DEFAULT_PAGE_SIZE}) removes the param — pass the ACTIVE VIEW's effective page
   * size so a URL param is only written when it actually overrides the view (WS-B Task 13:
   * a view carrying pageSize 100 needs an explicit `pageSize=50` to be beaten back to 50).
   */
  setPageSize: (pageSize: number, omitWhen?: number) => void;
  /** Activate a view: clears every override param; the default view key omits `view=`. */
  selectView: (key: string) => void;
  /**
   * Group by a column, or `null` to ungroup (removes BOTH keys — V1). A `granularity` is written only
   * for date columns; an unknown granularity in a URL is ignored (the grouping falls back to month).
   */
  setGroup: (group: ViewGroup | null) => void;
}

/** Remove every param this hook owns except `view` (the "clean slate" of spec §2). */
function clearOverrides(p: URLSearchParams): void {
  for (const key of [...p.keys()]) {
    if (key.startsWith(FILTER_PREFIX)) p.delete(key);
  }
  for (const key of OVERRIDE_SCALARS) p.delete(key);
}

function parsePositiveInt(raw: string | null): number | null {
  if (raw === null) return null;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 ? n : null;
}

export function useListSearchParams(defaultViewKey: string): ListSearchParams {
  const [searchParams, setSearchParams] = useSearchParams();

  const state = useMemo<ListSearchState>(() => {
    const filters: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      if (key.startsWith(FILTER_PREFIX)) filters[key] = value;
    });

    const sortCol = searchParams.get('sort');
    const rawView = searchParams.get('view');
    const groupCol = searchParams.get('group');
    const rawGranularity = searchParams.get('groupBy');
    const granularity = (GROUP_GRANULARITIES as readonly string[]).includes(rawGranularity ?? '')
      ? (rawGranularity as GroupGranularity)
      : null;
    return {
      view: rawView ?? defaultViewKey,
      rawView,
      filters,
      sort: sortCol ? { col: sortCol, dir: searchParams.get('dir') === 'desc' ? 'desc' : 'asc' } : null,
      page: parsePositiveInt(searchParams.get('page')) ?? 1,
      pageSize: parsePositiveInt(searchParams.get('pageSize')),
      group: groupCol ? { col: groupCol, granularity } : null,
    };
  }, [searchParams, defaultViewKey]);

  /** All mutations replace history (never push) and go through the functional form. */
  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setFilters = useCallback(
    (nextFilters: Record<string, string>) => {
      update((next) => {
        for (const key of [...next.keys()]) {
          if (key.startsWith(FILTER_PREFIX)) next.delete(key);
        }
        for (const [key, value] of Object.entries(nextFilters)) next.set(key, value);
        next.delete('page');
      });
    },
    [update]
  );

  const setSort = useCallback(
    (sort: ViewSort | null) => {
      update((next) => {
        if (sort) {
          next.set('sort', sort.col);
          next.set('dir', sort.dir);
        } else {
          next.delete('sort');
          next.delete('dir');
        }
        next.delete('page');
      });
    },
    [update]
  );

  const setPage = useCallback(
    (page: number) => {
      update((next) => {
        if (page > 1) next.set('page', String(page));
        else next.delete('page');
      });
    },
    [update]
  );

  const setPageSize = useCallback(
    (pageSize: number, omitWhen: number = DEFAULT_PAGE_SIZE) => {
      update((next) => {
        if (pageSize === omitWhen) next.delete('pageSize');
        else next.set('pageSize', String(pageSize));
        next.delete('page');
      });
    },
    [update]
  );

  const selectView = useCallback(
    (key: string) => {
      update((next) => {
        clearOverrides(next);
        if (key === defaultViewKey) next.delete('view');
        else next.set('view', key);
      });
    },
    [update, defaultViewKey]
  );

  const setGroup = useCallback(
    (group: ViewGroup | null) => {
      update((next) => {
        if (group) {
          next.set('group', group.col);
          if (group.granularity) next.set('groupBy', group.granularity);
          else next.delete('groupBy');
        } else {
          next.delete('group');
          next.delete('groupBy');
        }
        next.delete('page');
      });
    },
    [update]
  );

  return { ...state, setFilters, setSort, setPage, setPageSize, selectView, setGroup };
}
